import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';

function formatLeadTime(mins) {
  if (mins % 60 === 0) {
    const h = mins / 60;
    return `${h} hour${h === 1 ? '' : 's'}`;
  }
  return `${mins} minutes`;
}

export default function Account() {
  const { user, changePassword, logout } = useAuth();
  const api = useApi();

  const [nameVal, setNameVal] = useState(user?.name || '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState(null);

  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const [reminders, setReminders] = useState(null);
  const [hasConsented, setHasConsented] = useState(false);
  const [reminderMinsBefore, setReminderMinsBefore] = useState(120);
  const [showConsentStep, setShowConsentStep] = useState(false);
  const [remindersSaving, setRemindersSaving] = useState(false);
  const [remindersMsg, setRemindersMsg] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.getPrefs(user.sub)
      .then(d => { setReminders(d.emailReminders); setHasConsented(d.hasConsented); if (d.reminderMinsBefore) setReminderMinsBefore(d.reminderMinsBefore); })
      .catch(() => { setReminders(false); setHasConsented(false); });
  }, [user?.sub]);

  const handleSaveName = async (e) => {
    e.preventDefault();
    const trimmed = nameVal.trim();
    if (!trimmed) return;
    setNameSaving(true); setNameMsg(null);
    try {
      await api.updateProfile(user.sub, { name: trimmed });
      setNameMsg({ ok: true, text: 'Display name updated.' });
    } catch (err) {
      setNameMsg({ ok: false, text: err.message || 'Failed to update name.' });
    } finally {
      setNameSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      setPwMsg({ ok: false, text: 'New passwords do not match.' });
      return;
    }
    setPwSaving(true); setPwMsg(null);
    try {
      await changePassword(oldPw, newPw);
      setPwMsg({ ok: true, text: 'Password changed successfully.' });
      setOldPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setPwMsg({ ok: false, text: err.message || 'Failed to change password.' });
    } finally {
      setPwSaving(false);
    }
  };

  const handleToggleReminders = async (newVal) => {
    if (newVal && !hasConsented) { setShowConsentStep(true); return; }
    setRemindersSaving(true); setRemindersMsg(null);
    try {
      await api.updatePrefs(user.sub, { emailReminders: newVal });
      setReminders(newVal);
      setRemindersMsg({ ok: true, text: newVal ? 'Email reminders enabled.' : 'Email reminders disabled.' });
    } catch (err) {
      setRemindersMsg({ ok: false, text: err.message || 'Failed to update preferences.' });
    } finally {
      setRemindersSaving(false);
    }
  };

  const handleAcceptAndEnable = async () => {
    setRemindersSaving(true); setRemindersMsg(null);
    try {
      await api.recordConsent(user.sub);
      await api.updatePrefs(user.sub, { emailReminders: true });
      setHasConsented(true);
      setReminders(true);
      setShowConsentStep(false);
      setRemindersMsg({ ok: true, text: 'Email reminders enabled.' });
    } catch (err) {
      setRemindersMsg({ ok: false, text: err.message || 'Failed to enable reminders.' });
    } finally {
      setRemindersSaving(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (deleteConfirm !== 'DELETE') return;
    setDeleting(true); setDeleteMsg(null);
    try {
      await api.deleteAccount(user.sub);
      logout();
    } catch (err) {
      setDeleteMsg({ ok: false, text: err.message || 'Failed to delete account. Please try again.' });
      setDeleting(false);
    }
  };

  return (
    <div className="page-container">
      <h2 style={{ marginBottom: '1.5rem' }}>Account</h2>

      {/* Display name */}
      <div className="card" style={{ marginBottom: '1.5rem', maxWidth: '480px' }}>
        <div className="section-kicker" style={{ marginBottom: '1rem' }}>
          Display Name
        </div>
        <form onSubmit={handleSaveName} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div className="field">
            <label>Name shown on the leaderboard</label>
            <input
              type="text"
              value={nameVal}
              onChange={e => setNameVal(e.target.value)}
              maxLength={50}
              required
            />
          </div>
          {nameMsg && (
            <p style={{ margin: 0, fontSize: '0.85rem', color: nameMsg.ok ? 'var(--volt)' : 'var(--danger)' }}>
              {nameMsg.text}
            </p>
          )}
          <button type="submit" className="btn btn-primary" disabled={nameSaving || !nameVal.trim() || nameVal.trim() === user?.name}>
            {nameSaving ? 'Saving…' : 'Save Name'}
          </button>
        </form>
      </div>

      {/* Change password */}
      <div className="card" style={{ marginBottom: '1.5rem', maxWidth: '480px' }}>
        <div className="section-kicker" style={{ marginBottom: '1rem' }}>
          Change Password
        </div>
        <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div className="field">
            <label>Current password</label>
            <input type="password" value={oldPw} onChange={e => setOldPw(e.target.value)} required autoComplete="current-password" />
          </div>
          <div className="field">
            <label>New password <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>(min 10 chars, uppercase, number, symbol)</span></label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required autoComplete="new-password" />
          </div>
          <div className="field">
            <label>Confirm new password</label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required autoComplete="new-password" />
          </div>
          {pwMsg && (
            <p style={{ margin: 0, fontSize: '0.85rem', color: pwMsg.ok ? 'var(--volt)' : 'var(--danger)' }}>
              {pwMsg.text}
            </p>
          )}
          <button type="submit" className="btn btn-primary" disabled={pwSaving || !oldPw || !newPw || !confirmPw}>
            {pwSaving ? 'Changing…' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Consent modal */}
      {showConsentStep && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '440px', width: '100%' }}>
            <div className="section-kicker" style={{ marginBottom: '1rem' }}>
              Enable Email Reminders
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)', marginBottom: '0.75rem' }}>
              We'll send you a reminder email roughly {formatLeadTime(reminderMinsBefore)} before each matchday when you have unpredicted matches.
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)', marginBottom: '1.25rem' }}>
              By enabling reminders you consent to us using your email address for this purpose. Read our{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--volt)' }}>Privacy Notice</Link>{' '}
              to understand how your data is used. You can disable reminders at any time from this page.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="btn btn-primary" disabled={remindersSaving} onClick={handleAcceptAndEnable}>
                {remindersSaving ? 'Saving…' : 'I Accept — Enable Reminders'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowConsentStep(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email reminders */}
      <div className="card" style={{ marginBottom: '1.5rem', maxWidth: '480px' }}>
        <div className="section-kicker" style={{ marginBottom: '1rem' }}>
          Email Reminders
        </div>
        {reminders === null ? (
          <p style={{ color: 'var(--text-faint)', fontSize: 'var(--t-sm)' }}>Loading…</p>
        ) : (
          <>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)', marginBottom: '1rem' }}>
              Receive an email roughly {formatLeadTime(reminderMinsBefore)} before each matchday when you have unpredicted matches.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                className={`btn ${reminders ? 'btn-primary' : 'btn-secondary'}`}
                disabled={remindersSaving || reminders === true}
                onClick={() => handleToggleReminders(true)}
              >
                Enable
              </button>
              <button
                className={`btn ${!reminders ? 'btn-primary' : 'btn-secondary'}`}
                disabled={remindersSaving || reminders === false}
                onClick={() => handleToggleReminders(false)}
              >
                Disable
              </button>
            </div>
            {remindersMsg && (
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem', color: remindersMsg.ok ? 'var(--volt)' : 'var(--danger)' }}>
                {remindersMsg.text}
              </p>
            )}
          </>
        )}
      </div>

      {/* Delete account */}
      <div className="card" style={{ maxWidth: '480px', borderColor: 'var(--danger)' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--danger)', marginBottom: '1rem' }}>
          Delete Account
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)', marginBottom: '1rem' }}>
          Permanently deletes your account and all predictions. This cannot be undone.
          Type <strong>DELETE</strong> to confirm.
        </p>
        <form onSubmit={handleDeleteAccount} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div className="field">
            <input
              type="text"
              placeholder="Type DELETE to confirm"
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              autoComplete="off"
            />
          </div>
          {deleteMsg && (
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--danger)' }}>
              {deleteMsg.text}
            </p>
          )}
          <button
            type="submit"
            className="btn"
            style={{ background: 'var(--danger)', color: '#fff' }}
            disabled={deleting || deleteConfirm !== 'DELETE'}
          >
            {deleting ? 'Deleting…' : 'Delete My Account'}
          </button>
        </form>
      </div>
      {/* Privacy notice link */}
      <p style={{ fontSize: 'var(--t-xs)', color: 'var(--text-faint)', marginTop: '2rem' }}>
        <Link to="/privacy" style={{ color: 'var(--text-faint)' }}>Privacy Notice</Link>
        {' '}— how your data is used.
      </p>
    </div>
  );
}
